/**
 * Last Line Underlines Handler
 * Makes yellow underlines match the width of the last line of text
 */

class LastLineUnderlines {
  constructor() {
    this.selectors = [
      '.section-heading__title--underlined',
      '.ir__head--underlined',
      '.wi-roice__head--underlined',
      '.iwt__head--underlined',
      '.ugc-roice__head--underlined',
      '.htu-sec__head--underlined',
      '.jdgm-rev-widg__title',
      '[class*="--underlined"]',
      '.popup-form__heading'
    ];
    
    this.init();
  }

  init() {
    // Run on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.updateUnderlines());
    } else {
      this.updateUnderlines();
    }

    // Run on window resize with debouncing
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.updateUnderlines(), 250);
    });

    // Run on font load (in case web fonts affect text width)
    document.fonts.ready.then(() => {
      setTimeout(() => this.updateUnderlines(), 100);
    });

    // Watch for visibility changes (for popups and dynamic content)
    this.observeVisibilityChanges();
  }

  updateUnderlines() {
    // Find all underlined elements
    const underlinedElements = document.querySelectorAll(this.selectors.join(', '));
    
    underlinedElements.forEach(element => {
      this.updateUnderlineWidth(element);
    });
  }

  updateUnderlineWidth(element) {
    try {
      // Skip if element is not visible
      if (!this.isElementVisible(element)) {
        return;
      }

      // Create a range to measure text
      const range = document.createRange();
      range.selectNodeContents(element);
      const rects = range.getClientRects();

      if (rects.length > 0) {
        // Get the last line rectangle
        const lastRect = rects[rects.length - 1];
        const lastLineWidth = Math.ceil(lastRect.width);
        
        // Set the underline width to match the last line
        element.style.setProperty('--last-line-width', `${lastLineWidth}px`);
        
        // Add a class to indicate this element uses last-line width
        element.classList.add('underline-last-line');
        
        // Debug log (remove in production if needed)
        //console.log(`Updated underline for element:`, element, `Last line width: ${lastLineWidth}px`);
      }
    } catch (error) {
      console.warn('Error updating underline width:', error);
    }
  }

  isElementVisible(element) {
    // Check if element exists and is visible
    if (!element) return false;
    
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           style.opacity !== '0' &&
           element.offsetWidth > 0 && 
           element.offsetHeight > 0;
  }

  observeVisibilityChanges() {
    // Create a MutationObserver to watch for changes in the DOM
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      mutations.forEach((mutation) => {
        // Check if any added nodes or attribute changes affect our selectors
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldUpdate = true;
        } else if (mutation.type === 'attributes') {
          const target = mutation.target;
          // Check if the changed element or its children match our selectors
          if (target.matches && target.matches(this.selectors.join(', '))) {
            shouldUpdate = true;
          } else if (target.querySelector) {
            const hasUnderlinedChild = target.querySelector(this.selectors.join(', '));
            if (hasUnderlinedChild) {
              shouldUpdate = true;
            }
          }
        }
      });

      if (shouldUpdate) {
        // Debounce the update
        clearTimeout(this.observerTimeout);
        this.observerTimeout = setTimeout(() => this.updateUnderlines(), 50);
      }
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true,
      subtree: true
    });
  }

  // Method to manually trigger update (useful for dynamic content)
  refresh() {
    this.updateUnderlines();
  }
}

// Initialize the last line underlines
let lastLineUnderlines;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    lastLineUnderlines = new LastLineUnderlines();
  });
} else {
  lastLineUnderlines = new LastLineUnderlines();
}

// Export for global access if needed
window.LastLineUnderlines = LastLineUnderlines;
window.lastLineUnderlines = lastLineUnderlines;